import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import { useFromNumbers } from 'v2/hooks/use-from-numbers'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { ConvChannels } from 'v2/types/api/conversations'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { NodevarList } from '../components/nodevar-list'
import { CONFIRM_KEYS, v2validations } from '../config/validation.config'
import type { CallForwardNodeData, FlowData, SidebarComponentProps } from '../types'
import { confirmPromptFor } from '../utils/data-transformer'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = CallForwardNodeData['config']

// Node-level event callbacks for call forward (legacy PHLO). SER-4772.
const callForwardEventTypes = [
  { value: 'answer', label: 'Answer' },
  { value: 'hangup', label: 'Hangup' },
  { value: 'digits', label: 'Digits' },
  { value: 'record', label: 'Record' },
  { value: 'connected', label: 'Connected' },
  { value: 'completed', label: 'Completed' },
]

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
  isLegacy: boolean,
): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).call_forward
  return [
    {
      name: 'name',
      componentType: 'input',
      label: 'Name',
      zodValidation: validations.shape.name,
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'to',
      label: 'Forward call to',
      zodValidation: validations.shape.to,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter number',
        inputClassName: 'font-jetbrains text-s-400 tabular-nums',
        endIcon: (
          <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=to]" />
        ),
      },
    },
    {
      name: 'from',
      label: 'Caller ID for the receiver',
      zodValidation: validations.shape.from,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter number',
        inputClassName: 'font-jetbrains text-s-400 tabular-nums',
        endIcon: (
          <NodevarList
            nodevars={fieldsWithDynamicNodeVars.from}
            className="pr-2"
            inputElementSelector="[name=from]"
          />
        ),
      },
    },
    {
      name: 'is_validate_caller_id',
      label: 'Validate caller ID',
      componentType: 'switch',
      zodValidation: validations.shape.is_validate_caller_id,
      inputProps: {
        description: 'When enabled, the caller ID is validated before forwarding the call.',
      },
    },
    // Recording flag is node-level only for legacy/migrated flows; for other
    // flows the organization recording policy (CoreDB) decides, so it's hidden.
    ...(isLegacy
      ? ([
          {
            name: 'record',
            label: 'Record call',
            componentType: 'switch',
            zodValidation: validations.shape.record,
            inputProps: {
              'data-testid': 'switch-record-call',
              description:
                'Record the forwarded call. When unset, the organization recording policy applies.',
            },
          },
        ] satisfies GenericFieldConfig<FormValues>)
      : []),
    {
      name: 'extra_headers',
      label: 'Custom Headers',
      zodValidation: validations.shape.extra_headers,
      componentType: 'textarea',
      inputProps: {
        placeholder: 'X-PH-Key=value',
        className: 'font-jetbrains text-xs-400',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=extra_headers]"
          />
        ),
      },
    },
    {
      name: 'ring_timeout',
      label: 'Ring timeout',
      zodValidation: validations.shape.ring_timeout,
      componentType: 'inputNumber',
      inputProps: {
        type: 'number',
        placeholder: 'Enter timeout',
        inputClassName: 'z-[10] relative bg-transparent font-jetbrains text-s-400 tabular-nums',
        endIcon: (
          <span className="font-inter text-s-400 relative right-7 text-secondary">seconds</span>
        ),
      },
    },
    // Call screening (SER-6328): the receiver must press a key before the leg
    // bridges, so a carrier voicemail can never accept a forwarded call.
    {
      name: 'confirm_sound',
      label: 'Call screening',
      componentType: 'switch',
      zodValidation: validations.shape.confirm_sound,
      inputProps: {
        'data-testid': 'switch-call-screening',
        description:
          'The receiver hears a prompt and must press a key to accept. Unanswered legs follow the No Answer branch.',
      },
    },
    {
      name: 'confirm_key',
      label: 'Receiver presses',
      componentType: 'select',
      zodValidation: validations.shape.confirm_key,
      visible: (state) => state.confirm_sound === true,
      onChangeListenTo: ['confirm_sound'],
      inputProps: {
        'data-testid': 'select-confirm-key',
        searchable: false,
        options: CONFIRM_KEYS.map((key) => ({ label: key, value: key })),
      },
    },
    {
      name: 'confirm_prompt.text',
      label: 'Screening prompt',
      componentType: 'textarea',
      visible: (state) => state.confirm_sound === true,
      onChangeListenTo: ['confirm_sound', 'confirm_key'],
      inputProps: { 'data-testid': 'textarea-confirm-prompt' },
      // empty text is back-filled with this exact sentence on save
      getDynamicProps: (state) => ({
        inputProps: { placeholder: confirmPromptFor(state.confirm_key) },
      }),
    },
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={callForwardEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

export const CallForwardSidebarComponent = (props: SidebarComponentProps<CallForwardNodeData>) => {
  const { nodeVars } = props
  const { id: flowId } = useParams()
  const flowData = flowId
    ? queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(flowId))
    : undefined
  const isLegacy = !!flowData?.phlo?.is_legacy
  const { accountNumbers } = useFromNumbers(ConvChannels.CALL)
  const fieldsWithDynamicNodeVars = {
    from: [...(accountNumbers?.map((number) => number.number) || []), ...nodeVars],
  }
  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars, isLegacy)

  // No defaultValues here: sidebar.base merges them OVER the node's saved config,
  // which reset ring_timeout to 20 on every panel open (SER-5881). New nodes get
  // ring_timeout seeded via defaultNodeData, legacy loads via fillNullDefaults.
  return <GenericSidebarComponent fields={formFields} {...props} />
}
